import { useCallback } from 'react';
import { generatePath } from 'react-router';
import { RecurrencyOptionDto } from '@tiendanube/common';
import { CheckoutPaymentMethod } from '@tiendanube/common/src/enums';
import { useNavegate } from 'App/hooks';
import { combineStatus } from 'commons/utils/combineStatus';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import {
  usePayorderToPay,
  useUpdateCharge,
  usePaymentSetup,
  useAfterPaymentRedirection,
} from 'domains/Billing/Checkout/hooks';
import { paymentMethodIsMercadoPago } from '../../utils';

function useContinuePayment() {
  const { payPayOrder: payPayOrderPerMethod, extraStatus } = usePaymentSetup();
  const { updateCharge, status: updateSubscriptionsStatus } = useUpdateCharge();
  const { goTo } = useNavegate();
  const { passOnActionToPath } = useAfterPaymentRedirection();

  const { payorderToPay, isForCurrentPlan } = usePayorderToPay();

  const statusObject = combineStatus(updateSubscriptionsStatus, extraStatus);

  const onPay = useCallback(
    async (
      paymentMethod: CheckoutPaymentMethod,
      recurrencyOption?: RecurrencyOptionDto,
    ) => {
      if (!payorderToPay || statusObject.isLoading) return;
      if (isForCurrentPlan && recurrencyOption) {
        await updateCharge({
          method: paymentMethodIsMercadoPago(paymentMethod)
            ? CheckoutPaymentMethod.TICKET
            : paymentMethod,
          recurringFrequency: recurrencyOption.recurringFrequency,
          recurringInterval: recurrencyOption.recurringInterval,
        }).unwrap();
      }
      const payPayOrder = payPayOrderPerMethod[paymentMethod];
      payPayOrder(paymentMethod, payorderToPay.id);
    },
    [
      payPayOrderPerMethod,
      payorderToPay,
      isForCurrentPlan,
      updateCharge,
      statusObject,
    ],
  );

  const goToRecurrencies = useCallback(
    (payOrderId: string, method: CheckoutPaymentMethod) => {
      goTo(
        passOnActionToPath(
          `${generatePath(CHECKOUT_ROUTES.choosePaymentRecurrency, {
            payOrderId,
          })}?method=${method}`,
        ),
      );
    },
    [goTo, passOnActionToPath],
  );

  return {
    ...statusObject,
    onPay,
    goToRecurrencies,
  };
}

export default useContinuePayment;
